import { api } from './api';

// Admin panel: accounts, allowances, permissions and server-wide invites.

export type UserRole = 'admin' | 'member';

/** An account as the admin sees it. Byte counts: 0 = unlimited allowance, null = server default. */
export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  isAdmin: boolean;
  disabled: boolean;
  canInvite: boolean;
  canShare: boolean;
  quotaBytes: number | null;
  effectiveQuotaBytes: number;
  usedBytes: number;
  boards: number;
  invitedBy: string | null;
  createdAt: string;
  lastLoginAt: string | null;
}

/** Presets an invite applies to the account it creates; a missing key means the default. */
export interface InviteGrants {
  quotaBytes?: number | null;
  canInvite?: boolean;
  canShare?: boolean;
}

export interface AdminInvite {
  code: string;
  expiresAt: string;
  createdAt: string;
  createdBy: string | null;
  note: string | null;
  grants: InviteGrants;
}

export interface ServerInfo {
  defaultQuotaBytes: number;
  maxUploadBytes: number;
  usedBytes: number;
  diskFreeBytes: number | null;
  diskTotalBytes: number | null;
}

export interface AdminState {
  users: AdminUser[];
  invites: AdminInvite[];
  server: ServerInfo;
}

export interface UserPatch {
  name?: string;
  role?: UserRole;
  disabled?: boolean;
  canInvite?: boolean;
  canShare?: boolean;
  quotaBytes?: number | null;
}

export const loadAdmin = () => api.get<AdminState>('/admin');
export const updateUser = (id: string, patch: UserPatch) =>
  api.patch<{ user: AdminUser }>(`/admin/users/${id}`, patch).then((r) => r.user);
export const deleteUser = (id: string) => api.delete<{ ok: true }>(`/admin/users/${id}`);
export const createAdminInvite = (input: { note?: string; grants: InviteGrants; expiresDays?: number }) =>
  api.post<{ invite: AdminInvite }>('/admin/invites', input).then((r) => r.invite);
export const revokeInvite = (code: string) => api.delete<{ ok: true }>(`/invites/${encodeURIComponent(code)}`);
